import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LotSizingProcedure = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('lot_sizing_procedures');
  const [viewItem, setViewItem] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);

  useEffect(() => {
    loadProcedures();
  }, []);

  const loadProcedures = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/lot-sizing-procedures`);
      setProcedures(response.data || []);
    } catch (error) {
      console.error('Error loading lot sizing procedures:', error);
      toast.error('Failed to load lot sizing procedures');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (procedure = null) => {
    setEditingProcedure(procedure);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      lot_sizing_procedure_id: (formData.get('lot_sizing_procedure_id') || '').toUpperCase(),
      description: formData.get('description') || ''
    };

    try {
      if (editingProcedure) {
        await axios.put(`${BACKEND_URL}/api/lot-sizing-procedures/${editingProcedure.lot_sizing_procedure_id}`, {
          description: data.description
        });
        toast.success('Lot Sizing Procedure updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/lot-sizing-procedures`, data);
        toast.success('Lot Sizing Procedure created successfully');
      }
      setIsDialogOpen(false);
      loadProcedures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save lot sizing procedure');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lot Sizing Procedures</h1>
            <p className="text-sm text-gray-500 mt-1">Manage lot sizing procedure types</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Lot Sizing Procedures</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Procedure
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Procedure ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {procedures.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-gray-500">
                        No lot sizing procedures found. Click &quot;Add Procedure&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    procedures.map((procedure) => (
                      <tr key={procedure.lot_sizing_procedure_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{procedure.lot_sizing_procedure_id}</td>
                        <td className="py-3 px-4 text-sm">{procedure.description}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="lot_sizing_procedures" onView={() => setViewItem(procedure)} onEdit={() => handleOpenDialog(procedure)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingProcedure ? 'Edit Lot Sizing Procedure' : 'Add Lot Sizing Procedure'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lot_sizing_procedure_id">Procedure ID * (1 character)</Label>
                  <Input
                    id="lot_sizing_procedure_id"
                    name="lot_sizing_procedure_id"
                    defaultValue={editingProcedure?.lot_sizing_procedure_id || ''}
                    placeholder="e.g., W, F, E"
                    maxLength={1}
                    minLength={1}
                    pattern="[A-Za-z]{1}"
                    title="Must be exactly 1 letter"
                    disabled={!!editingProcedure}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 1);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Single letter (W, F, E, etc.)</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingProcedure?.description || ''}
                    placeholder="e.g., Fixed lot size"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingProcedure ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default LotSizingProcedure;
